import { signIn, confirmSignIn, fetchAuthSession, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

/**
 * Runner-pool sign-in flow. Applicants are provisioned at submit (spec §5
 * stage 3) and arrive with a Cognito invitation's temporary password, so a
 * first sign-in walks: temp password → NEW_PASSWORD_REQUIRED → done. MFA is
 * OPTIONAL on the Runner pool today (auth-stack.ts) — a TOTP challenge is
 * surfaced as unsupported rather than silently mishandled.
 */
export type LoginStage =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'forgot' }
  | { kind: 'resetCode'; email: string }
  | { kind: 'done' };

export class UnsupportedStepError extends Error {
  constructor(step: string) {
    super(`Unsupported sign-in step: ${step}`);
    this.name = 'UnsupportedStepError';
  }
}

function toStage(step: string): LoginStage {
  switch (step) {
    case 'DONE':
      return { kind: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'newPassword' };
    default:
      throw new UnsupportedStepError(step);
  }
}

export async function startSignIn(email: string, password: string): Promise<LoginStage> {
  try {
    const out = await signIn({ username: email, password });
    return toStage(out.nextStep.signInStep);
  } catch (err) {
    if ((err as { name?: string })?.name === 'UserAlreadyAuthenticatedException') return { kind: 'done' };
    throw err;
  }
}

export async function answerNewPassword(password: string): Promise<LoginStage> {
  const out = await confirmSignIn({ challengeResponse: password });
  return toStage(out.nextStep.signInStep);
}

export async function startReset(email: string, locale: 'en' | 'es' = 'en'): Promise<LoginStage> {
  try { await resetPassword({ username: email, options: { clientMetadata: { locale } } }); }
  catch (error) { if ((error as { name?: string }).name !== 'UserNotFoundException') throw error; }
  return { kind: 'resetCode', email };
}

export async function finishReset(email: string, code: string, newPassword: string): Promise<LoginStage> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  return { kind: 'credentials' };
}

export async function hasSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return Boolean(session.tokens?.idToken);
  } catch {
    return false;
  }
}

/** Same-origin relative paths only. */
export function safeNextPath(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return '/';
  return raw;
}
